import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Clock, Car, Navigation, ExternalLink, Check } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { COLORS, IMAGES, GOOGLE_MAPS_LINK, fadeInUp, staggerContainer, scaleIn } from '../config';

const contactIcons = [
  <MapPin key="0" size={32} />,
];

const contactLinks = [
  GOOGLE_MAPS_LINK,
];

const DoveSiamo = () => {
  const { t } = useLanguage();
  const contactItems = t('doveSiamoPage.contact.items');

  return (
    <>
      {/* Hero Banner */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.bologna})` }}
        />
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, rgba(114, 47, 55, 0.9) 0%, rgba(30, 58, 95, 0.85) 100%)` }} />
        <div className="relative z-10 text-center px-4">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-['Cormorant_Garamond'] text-5xl md:text-6xl text-white mb-4"
          >
            {t('doveSiamoPage.hero.title')}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl italic"
            style={{ color: COLORS.gold }}
          >
            {t('doveSiamoPage.hero.subtitle')}
          </motion.p>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-24" style={{ backgroundColor: COLORS.vaniglia }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Map */}
            <motion.div
              variants={scaleIn}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="rounded-xl overflow-hidden shadow-xl h-[400px] lg:h-[500px]"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2845.5!2d11.352!3d44.501!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x477fd4949c64e55d%3A0x0!2sVia%20Jacopo%20della%20Quercia%202%2F2D%2C%2040128%20Bologna%20BO!5e0!3m2!1sit!2sit!4v1700000000000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Lola la Liberta Location"
              />
            </motion.div>

            {/* Info */}
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <motion.p
                variants={fadeInUp}
                className="text-xs tracking-[0.2em] font-semibold uppercase mb-4"
                style={{ color: COLORS.redwine }}
              >
                {t('location.overline')}
              </motion.p>
              <motion.h2
                variants={fadeInUp}
                className="font-['Cormorant_Garamond'] text-4xl md:text-5xl mb-6"
                style={{ color: COLORS.redwine }}
              >
                {t('location.title')}
              </motion.h2>
              <motion.div variants={fadeInUp} className="w-16 h-1 mb-8" style={{ backgroundColor: COLORS.gold }} />

              <motion.div variants={fadeInUp} className="space-y-6">
                {/* Address */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${COLORS.redwine}15` }}>
                    <MapPin size={24} style={{ color: COLORS.redwine }} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: COLORS.redwine }}>{t('location.info.address.title')}</h3>
                    <p style={{ color: COLORS.nero }}>{t('location.address')}</p>
                    <p style={{ color: `${COLORS.nero}99` }}>{t('location.city')}</p>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${COLORS.redwine}15` }}>
                    <Clock size={24} style={{ color: COLORS.redwine }} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: COLORS.redwine }}>{t('location.info.hours.title')}</h3>
                    <p style={{ color: COLORS.nero }}>{t('location.info.hours.weekdays')}</p>
                    <p style={{ color: `${COLORS.nero}99` }}>{t('doveSiamoPage.labels.lunch')}: {t('location.info.hours.lunch')}</p>
                  </div>
                </div>

                {/* Parking */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${COLORS.redwine}15` }}>
                    <Car size={24} style={{ color: COLORS.redwine }} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: COLORS.redwine }}>{t('location.info.parking.title')}</h3>
                    <p className="flex items-center gap-2" style={{ color: COLORS.nero }}>
                      <Check size={14} style={{ color: COLORS.gold }} />
                      {t('location.info.parking.line1')}
                    </p>
                    <p className="flex items-center gap-2" style={{ color: `${COLORS.nero}99` }}>
                      <Check size={14} style={{ color: COLORS.gold }} />
                      {t('location.info.parking.line2')}
                    </p>
                  </div>
                </div>

                {/* CTA Button */}
                <motion.div variants={fadeInUp} className="pt-4">
                  <a
                    href={GOOGLE_MAPS_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold transition-all hover:brightness-110"
                    style={{ backgroundColor: COLORS.gold, color: COLORS.redwine }}
                  >
                    <Navigation size={20} />
                    {t('location.openMaps')}
                    <ExternalLink size={16} />
                  </a>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24" style={{ backgroundColor: COLORS.redwine }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.h2
              variants={fadeInUp}
              className="font-['Cormorant_Garamond'] text-4xl md:text-5xl mb-4"
              style={{ color: COLORS.vaniglia }}
            >
              {t('doveSiamoPage.contact.title')}
            </motion.h2>
            <motion.div variants={fadeInUp} className="w-16 h-1 mx-auto" style={{ backgroundColor: COLORS.gold }} />
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="flex justify-center"
          >
            {Array.isArray(contactItems) && contactItems.map((contact, index) => {
              const link = contactLinks[index] || GOOGLE_MAPS_LINK;
              return (
                <motion.a
                  key={index}
                  href={link}
                  target={link.startsWith('http') ? '_blank' : undefined}
                  rel={link.startsWith('http') ? 'noopener noreferrer' : undefined}
                  variants={scaleIn}
                  className="text-center p-8 rounded-xl transition-all hover:scale-105 w-full max-w-md"
                  style={{ backgroundColor: `${COLORS.vaniglia}10`, border: `1px solid ${COLORS.vaniglia}15` }}
                >
                  <div className="mb-4 flex justify-center" style={{ color: COLORS.gold }}>{contactIcons[index]}</div>
                  <h3 className="font-semibold text-lg mb-2" style={{ color: COLORS.vaniglia }}>{contact.title}</h3>
                  <p className="text-sm" style={{ color: `${COLORS.vaniglia}99` }}>{contact.info}</p>
                </motion.a>
              );
            })}
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default DoveSiamo;
